import { transparentPixel } from "@utils/string";
import { useFormatter, useLocale, useTranslations } from "next-intl";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useContext, useEffect, useMemo, useState } from "react";

import CardTags from "@/components/AutogeneratedCarousel/CardTags";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
import Image from "@/components/ui/Image/Image";
import { UserContext } from "@/context/UserContext";
import { propertyCardClick } from "@/dataLayer/dataLayer";
import useMediaQuery from "@/hooks/useMediaQuery";
import { useProjectUrl } from "@/hooks/useProjectUrl";
import useResponsiveLabel from "@/hooks/useResponsiveLabel";
import { ProjectCardListProps } from "@/types";
import cn from "@/utils/cn";

export default function ProjectCardList({
  item,
  customClass,
  displayMapPin = false,
  onPinSelected,
  finalUrl,
  projectResultPage = false,
}: ProjectCardListProps) {
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const { currency } = useContext(UserContext);
  const t = useTranslations();
  const format = useFormatter();
  const router = useRouter();
  const locale = useLocale();
  const formattedPrice = format
    .number(Math.round(Number(item.price) / 100) || 0, {
      style: "currency",
      currency: currency,
      trailingZeroDisplay: "stripIfInteger",
    })
    ?.replace(/(\p{Sc})(\d)/u, "$1 $2");
  const fromLabel = useResponsiveLabel(t("project.mobile.from", { 0: formattedPrice }), t("project.desktop.from", { 0: formattedPrice }));

  // Communities matching and creating a URL - Start
  const finalUrlForCommunity = useProjectUrl({ item, locale });
  // console.log(
  //   "projectResultPage ? finalUrl : finalUrlForCommunity",
  //   projectResultPage ? finalUrl : finalUrlForCommunity,
  //   finalUrl,
  //   finalUrlForCommunity,
  //   item,
  // );
  // Communities matching and creating a URL - End
  return (
    <article className={cn(`group flex flex-col relative z-0 rounded-lg`, customClass)}>
      <div className="relative" style={{ height: isDesktop ? undefined : "calc(100% - 50px)" }}>
        <div className="relative rounded-lg overflow-hidden">
          <Image
            key={`image-0`}
            alt={item.images?.[0]?.title || ""}
            className="aspect-4-3 md:aspect-9-16 transition transform hover:duration-[6000ms] md:group-hover:scale-105 motion-reduce:transition-none motion-reduce:hover:transform-none w-full h-full"
            height={527}
            width={298}
            src={item.images?.[0]?.url || transparentPixel}
          />
          <div className="absolute w-full h-full top-0 left-0 inset-0 img-overlay-2"></div>
        </div>

        <div className="absolute z-10 top-0 left-0 w-full h-full flex flex-row-reverse items-end justify-end gap-xs mt-auto md:flex-col md:items-start  text-white">
          <div className="md:w-full md:rounded-lg md:linear-gradient(181deg, rgba(0, 0, 0, 0.30) 1.08%, rgba(0, 0, 0, 0.00) 80.87%)">
            {item.logo && (
              <Image
                src={item.logo.url}
                width={120}
                height={60}
                alt={item.logo.title || ""}
                className="mx-auto w-full mb-l md:mt-l max-w-[100px] max-h-[60px]"
              />
            )}
          </div>
          <div
            onClick={() => {
              if (isDesktop) return;
              if (item.cta && !item?.cta?.link?.url?.includes("#")) {
                propertyCardClick({
                  card_name: item.title,
                  project_name: item.title,
                  project_location: item.location || "",
                  project_type: item.unitType || "",
                  tag_name: item.tags?.map((tag) => tag.label).join(",") || "",
                  bedrooms_number: `${item.bedrooms.value}`,
                  property_category: item.unitType || "",
                  property_indicative_price: formattedPrice || "",
                });
                router.push(projectResultPage ? finalUrl : finalUrlForCommunity);
                // router.push(item.cta.link.url);
              }
            }}
            className="carousel-projects-card flex flex-col flex-[2] gap-xs px-s pt-s pb-m text-s font-secondary-light h-1/2 justify-end md:h-auto  md:mt-auto md:w-full"
          >
            {item.tags && item.tags.length > 0 && <CardTags tags={item.tags} />}
            <h3 className="font-medium text-m">{item.title}</h3>
            {!!item.location && <p>{item.location}</p>}
            {!!item.price && <p className="hidden font-normal md:block">{fromLabel}</p>}
            {Object.keys(item.bedrooms).length > 0 && item.bedrooms.value != "0" && (
              <p className="hidden md:block">
                {item.bedrooms.value} {item.bedrooms.label}
                {item.bedrooms.value !== "" && item.unitType ? `, ${item.unitType}` : ""}
              </p>
            )}
            {item.cta && !item?.cta?.link?.url?.includes("#") && (
              <div className="items-center hidden font-medium md:group-hover:flex mt-s">
                <Button
                  href={projectResultPage ? finalUrl : finalUrlForCommunity}
                  size={ButtonSize.SMALL}
                  theme={ButtonTheme.WHITE}
                  type="button"
                  variant={ButtonVariant.PRIMARY}
                  customClass="text-center !border-white hover:!border-0"
                >
                  {item.cta.label}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="relative z-20 p-s text-s md:hidden">
        {!!item.price && <p className="font-medium">{fromLabel}</p>}
        {Object.keys(item.bedrooms).length > 0 && (
          <p>
            {Number(item.bedrooms.value) > 0 && `${item.bedrooms.value} ${item.bedrooms.label}`}
            {!!item.unitType && item.unitType !== "-" ? `, ${item.unitType}` : ""}
          </p>
        )}
      </div>
      {item.cta && !item?.cta?.link?.url?.includes("#") && (
        <Link
          onClick={() =>
            propertyCardClick({
              card_name: item.title,
              project_name: item.title,
              project_location: item.location || "",
              project_type: item.unitType || "",
              tag_name: item.tags?.map((tag) => tag.label).join(",") || "",
              bedrooms_number: `${item.bedrooms.value}`,
              property_category: item.unitType || "",
              property_indicative_price: formattedPrice || "",
            })
          }
          href={projectResultPage ? finalUrl : finalUrlForCommunity}
          // href={item.cta.link.url}
          className="absolute top-0 left-0 z-[5] w-full h-full"
        >
          <span className="sr-only">{item.cta.label}</span>
        </Link>
      )}
      {displayMapPin && (
        <button type="button" onClick={onPinSelected} className="absolute z-10 top-s right-s">
          <Icon name="geotag" size="24" className={item?.images?.[0]?.url.includes("placeholder") ? "stroke-black" : "stroke-white"} />
        </button>
      )}
    </article>
  );
}
